import React, { useState, useRef } from "react";
import API from "../../services/api.ts";
import { toast } from "react-toastify";

const DatasetZipUpload = ({ onPatientsCreated }) => {
    const [zipFile, setZipFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [description, setDescription] = useState("");
    const [isAnonymized, setIsAnonymized] = useState(false);
    const [isSynthetic, setIsSynthetic] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    const handleZipSelect = async (e) => {
        const file = e.target.files[0];

        if (!file) {
            return;
        }

        // Validate file type
        if (!file.name.endsWith('.zip')) {
            toast.error("Please select a ZIP file.");
            return;
        }

        setZipFile(file);
        setPreview(null);

        // Send to backend for parsing
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('zip_file', file);

            const response = await API.post('/data_manager/dataset-upload/parse-zip/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                }
            });

            setPreview(response.data);
            setUploadProgress(0);
        } catch (err) {
            console.error("Error parsing ZIP file:", err);
            const errorMsg = err.response?.data?.error || "Failed to parse ZIP file.";
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmUpload = async () => {
        if (!preview || preview.total_patients === 0) {
            toast.warning("No patients to upload.");
            return;
        }

        if (!zipFile) {
            toast.error("ZIP file not found. Please select again.");
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('zip_file', zipFile);
            formData.append('description', description);
            formData.append('is_anonymized', isAnonymized);
            formData.append('is_synthetic', isSynthetic);

            const response = await API.post('/data_manager/dataset-upload/confirm-zip/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                }
            });

            toast.success(`Successfully created ${response.data.total_patients} patients with ${response.data.total_files} data items!`);

            // Call callback with created patients
            if (onPatientsCreated) {
                onPatientsCreated(response.data.patients);
            }

            // Reset state
            handleCancel();
        } catch (err) {
            console.error("Error uploading ZIP dataset:", err);
            const errorMsg = err.response?.data?.error || "Failed to upload ZIP dataset.";
            toast.error(errorMsg);
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleCancel = () => {
        setZipFile(null);
        setPreview(null);
        setDescription("");
        setUploadProgress(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const getDataTypeIcon = (dataType) => {
        const icons = {
            imaging: 'fa-x-ray',
            image: 'fa-image',
            document: 'fa-file-text',
            table: 'fa-table',
            signal: 'fa-wave-square'
        };
        return icons[dataType] || 'fa-file';
    };

    const getDataTypeBadgeColor = (dataType) => {
        const colors = {
            imaging: 'primary',
            image: 'success',
            document: 'warning',
            table: 'info',
            signal: 'secondary'
        };
        return colors[dataType] || 'dark';
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    };

    return (
        <div className="dataset-zip-upload">
            {/* ZIP File Selection */}
            {!preview && (
                <div className="zip-select-area">
                    <div className="zip-dropzone">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".zip"
                            onChange={handleZipSelect}
                            className="zip-input"
                            id="zip-input"
                        />
                        <label htmlFor="zip-input" className="zip-label">
                            {loading ? (
                                <div className="loading-state">
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    <div>
                                        <div>Parsing ZIP file...</div>
                                        {uploadProgress > 0 && (
                                            <div className="progress mt-2" style={{ width: '200px' }}>
                                                <div
                                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                                    role="progressbar"
                                                    style={{ width: `${uploadProgress}%` }}
                                                >
                                                    {uploadProgress}%
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <i className="fa fa-file-archive fa-3x mb-3"></i>
                                    <h5>Upload ZIP Dataset</h5>
                                    <p className="text-muted">
                                        Select a ZIP file containing patient folders.
                                        <br />
                                        Each top-level folder name will be used as the Patient ID.
                                    </p>
                                    {zipFile && (
                                        <div className="selected-file mt-2">
                                            <i className="fa fa-file-archive me-2"></i>
                                            {zipFile.name} ({formatFileSize(zipFile.size)})
                                        </div>
                                    )}
                                    <span className="btn btn-outline-primary mt-2">
                                        <i className="fa fa-upload me-2"></i>
                                        Select ZIP File
                                    </span>
                                </>
                            )}
                        </label>
                    </div>
                    <div className="zip-structure-hint">
                        <h6><i className="fa fa-info-circle me-2"></i>Expected ZIP Structure:</h6>
                        <pre className="structure-example">
{`dataset.zip
├── Patient_001/
│   ├── imaging/
│   │   ├── scan.dcm
│   │   └── ct.nii.gz
│   ├── images/
│   │   └── photo.png
│   └── reports/
│       └── report.pdf
├── Patient_002/
│   ├── data.csv
│   └── notes.md
└── Patient_003/
    └── signal.edf`}
                        </pre>
                        <div className="hint-note mt-2">
                            <small className="text-muted">
                                <i className="fa fa-lightbulb me-1"></i>
                                Subfolder names (imaging/, images/, etc.) are optional.
                                File types are automatically detected.
                            </small>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Section */}
            {preview && (
                <div className="dataset-preview">
                    <div className="preview-header">
                        <h5>
                            <i className={`fa ${preview.total_patients > 0 ? 'fa-check-circle text-success' : 'fa-exclamation-triangle text-warning'} me-2`}></i>
                            Dataset Preview
                        </h5>
                        <div className="preview-stats">
                            <span className="stat-badge">
                                <i className="fa fa-file-archive me-1"></i>
                                {zipFile && formatFileSize(zipFile.size)}
                            </span>
                            <span className="stat-badge">
                                <i className="fa fa-users me-1"></i>
                                {preview.total_patients} Patients
                            </span>
                            <span className="stat-badge">
                                <i className="fa fa-file me-1"></i>
                                {preview.total_files} Files
                            </span>
                        </div>
                    </div>

                    {/* Debug info when no patients found */}
                    {preview.total_patients === 0 && preview.debug && (
                        <div className="debug-info alert alert-warning mb-3">
                            <h6><i className="fa fa-bug me-2"></i>Debug Info</h6>
                            <ul className="mb-2">
                                <li><strong>Bytes received by server:</strong> {preview.debug.actual_bytes_received ? formatFileSize(preview.debug.actual_bytes_received) : 'N/A'}</li>
                                <li>Total entries in ZIP: {preview.debug.total_entries}</li>
                                <li>Directories: {preview.debug.directories}</li>
                                <li>Hidden/system files skipped: {preview.debug.hidden_files}</li>
                                <li>Files at root level (skipped): {preview.debug.root_files}</li>
                                <li>Unrecognized file extensions: {preview.debug.unrecognized_files}</li>
                                <li>Recognized files: {preview.debug.recognized_files}</li>
                                {preview.debug.wrapper_folders && preview.debug.wrapper_folders.length > 0 && (
                                    <li>Wrapper folders detected: <code>{preview.debug.wrapper_folders.join(' → ')}</code></li>
                                )}
                                {preview.debug.patient_folder_depth !== undefined && (
                                    <li>Patient folder depth: {preview.debug.patient_folder_depth}</li>
                                )}
                            </ul>
                            {preview.debug.sample_paths && preview.debug.sample_paths.length > 0 && (
                                <div className="mb-2">
                                    <strong>Sample paths in ZIP:</strong>
                                    <ul className="mb-0">
                                        {preview.debug.sample_paths.map((path, idx) => (
                                            <li key={idx}><code>{path}</code></li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {preview.skipped_extensions && preview.skipped_extensions.length > 0 && (
                                <p className="mb-0">
                                    <strong>Unrecognized extensions:</strong> {preview.skipped_extensions.join(', ')}
                                </p>
                            )}
                            <hr />
                            <p className="mb-0 small">
                                <strong>Expected structure:</strong> Your ZIP should have patient folders at the top level,
                                with files inside each patient folder. Example: <code>Patient_001/scan.dcm</code>
                            </p>
                            {preview.debug.actual_bytes_received && preview.debug.actual_bytes_received < 1000 && (
                                <p className="mb-0 small text-danger mt-2">
                                    <i className="fa fa-exclamation-triangle me-1"></i>
                                    <strong>Warning:</strong> The file appears to be truncated or not fully uploaded.
                                    Please try uploading again. If using a large file, ensure your connection is stable.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Summary Stats by Data Type */}
                    {preview.summary && (
                        <div className="data-type-summary mb-3">
                            <div className="summary-title">Data Types Summary:</div>
                            <div className="summary-badges">
                                {Object.entries(preview.summary).map(([dataType, count]) => {
                                    if (count === 0) return null;
                                    return (
                                        <span key={dataType} className={`badge bg-${getDataTypeBadgeColor(dataType)} me-2`}>
                                            <i className={`fa ${getDataTypeIcon(dataType)} me-1`}></i>
                                            {dataType}: {count}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Options */}
                    <div className="upload-options mb-3">
                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label">Description (optional)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter description for all data items"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-4">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="zip-is-anonymized"
                                        checked={isAnonymized}
                                        onChange={(e) => setIsAnonymized(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="zip-is-anonymized">
                                        Anonymized Data
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-4">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="zip-is-synthetic"
                                        checked={isSynthetic}
                                        onChange={(e) => setIsSynthetic(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="zip-is-synthetic">
                                        Synthetic Data
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Patients List */}
                    <div className="patients-preview-list">
                        {preview.patients.map((patient, index) => (
                            <div key={index} className="patient-preview-card">
                                <div className="patient-preview-header">
                                    <div className="patient-id">
                                        <i className="fa fa-user me-2"></i>
                                        <strong>{patient.patient_id}</strong>
                                        {patient.is_existing && (
                                            <span className="badge bg-info ms-2" title="Patient already exists">
                                                <i className="fa fa-link me-1"></i>
                                                Existing
                                            </span>
                                        )}
                                    </div>
                                    <span className="file-count-badge">
                                        {patient.total_files} files
                                    </span>
                                </div>
                                <div className="patient-preview-data">
                                    {Object.entries(patient.data).map(([dataType, items]) => {
                                        if (items.length === 0) return null;
                                        return (
                                            <div key={dataType} className="data-type-group">
                                                <span className={`badge bg-${getDataTypeBadgeColor(dataType)} me-2`}>
                                                    <i className={`fa ${getDataTypeIcon(dataType)} me-1`}></i>
                                                    {dataType}: {items.length}
                                                </span>
                                                <div className="file-list">
                                                    {items.slice(0, 3).map((item, idx) => (
                                                        <span key={idx} className="file-item" title={item.filename}>
                                                            {item.filename}
                                                            <small className="text-muted ms-1">
                                                                ({formatFileSize(item.size)})
                                                            </small>
                                                        </span>
                                                    ))}
                                                    {items.length > 3 && (
                                                        <span className="text-muted more-files">
                                                            +{items.length - 3} more
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Upload Progress */}
                    {uploading && uploadProgress > 0 && (
                        <div className="upload-progress-bar mb-3">
                            <div className="progress">
                                <div
                                    className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                                    role="progressbar"
                                    style={{ width: `${uploadProgress}%` }}
                                >
                                    Uploading... {uploadProgress}%
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="preview-actions">
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2"
                            onClick={handleCancel}
                            disabled={uploading}
                        >
                            <i className="fa fa-times me-2"></i>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={handleConfirmUpload}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Creating Patients...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-check me-2"></i>
                                    Confirm & Create {preview.total_patients} Patients
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                .dataset-zip-upload {
                    padding: 1rem 0;
                }

                .zip-dropzone {
                    border: 2px dashed rgba(255, 255, 255, 0.3);
                    border-radius: 12px;
                    padding: 2rem;
                    text-align: center;
                    background: rgba(0, 0, 0, 0.2);
                    transition: all 0.3s ease;
                }

                .zip-dropzone:hover {
                    border-color: rgba(99, 102, 241, 0.5);
                    background: rgba(99, 102, 241, 0.1);
                }

                .zip-input {
                    display: none;
                }

                .zip-label {
                    cursor: pointer;
                    display: block;
                    color: rgba(255, 255, 255, 0.8);
                }

                .loading-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }

                .selected-file {
                    background: rgba(99, 102, 241, 0.2);
                    color: #a5b4fc;
                    padding: 0.5rem 1rem;
                    border-radius: 8px;
                    display: inline-block;
                }

                .zip-structure-hint {
                    margin-top: 1.5rem;
                    padding: 1rem;
                    background: rgba(0, 0, 0, 0.2);
                    border-radius: 8px;
                }

                .zip-structure-hint h6 {
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 0.5rem;
                }

                .structure-example {
                    background: rgba(0, 0, 0, 0.3);
                    padding: 1rem;
                    border-radius: 6px;
                    color: rgba(255, 255, 255, 0.6);
                    font-size: 0.85rem;
                    margin-bottom: 0;
                }

                .hint-note {
                    color: rgba(255, 255, 255, 0.5);
                }

                .preview-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;
                    padding-bottom: 1rem;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                    flex-wrap: wrap;
                    gap: 0.5rem;
                }

                .preview-header h5 {
                    margin: 0;
                    color: #fff;
                }

                .preview-stats {
                    display: flex;
                    gap: 0.75rem;
                    flex-wrap: wrap;
                }

                .stat-badge {
                    background: rgba(99, 102, 241, 0.2);
                    color: #a5b4fc;
                    padding: 0.4rem 0.8rem;
                    border-radius: 20px;
                    font-size: 0.85rem;
                }

                .data-type-summary {
                    background: rgba(255, 255, 255, 0.05);
                    padding: 0.75rem 1rem;
                    border-radius: 8px;
                }

                .summary-title {
                    color: rgba(255, 255, 255, 0.7);
                    font-size: 0.85rem;
                    margin-bottom: 0.5rem;
                }

                .summary-badges {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.25rem;
                }

                .patients-preview-list {
                    max-height: 400px;
                    overflow-y: auto;
                    margin-bottom: 1rem;
                }

                .patient-preview-card {
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 0.75rem;
                }

                .patient-preview-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.75rem;
                }

                .patient-id {
                    color: #fff;
                    font-size: 1rem;
                    display: flex;
                    align-items: center;
                }

                .file-count-badge {
                    background: rgba(255, 255, 255, 0.1);
                    color: rgba(255, 255, 255, 0.7);
                    padding: 0.25rem 0.5rem;
                    border-radius: 12px;
                    font-size: 0.75rem;
                }

                .patient-preview-data {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                }

                .data-type-group {
                    display: flex;
                    align-items: flex-start;
                    flex-wrap: wrap;
                    gap: 0.25rem;
                }

                .file-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.25rem;
                }

                .file-item {
                    background: rgba(255, 255, 255, 0.05);
                    color: rgba(255, 255, 255, 0.6);
                    padding: 0.2rem 0.5rem;
                    border-radius: 4px;
                    font-size: 0.75rem;
                    max-width: 200px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .more-files {
                    font-size: 0.75rem;
                    padding: 0.2rem 0.5rem;
                }

                .upload-options {
                    background: rgba(255, 255, 255, 0.05);
                    padding: 1rem;
                    border-radius: 8px;
                }

                .upload-progress-bar {
                    margin-top: 1rem;
                }

                .upload-progress-bar .progress {
                    height: 24px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 12px;
                }

                .preview-actions {
                    display: flex;
                    justify-content: flex-end;
                    padding-top: 1rem;
                    border-top: 1px solid rgba(255, 255, 255, 0.1);
                }
            `}</style>
        </div>
    );
};

export default DatasetZipUpload;
